import { useEffect, useRef, useState } from "react";

const diferenciais = [
  {
    icon: "ri-heart-2-line",
    title: "Atendimento Humanizado",
    desc: "Tratamos cada caso com empatia e atenção individual, entendendo as necessidades reais de cada cliente.",
    highlight: "Você não é apenas um processo – é uma pessoa.",
  },
  {
    icon: "ri-stethoscope-line",
    title: "Especialização na Área da Saúde",
    desc: "Profundo conhecimento em Direito Médico e Saúde, combinando expertise jurídica com entendimento do setor.",
    highlight: "Referência em direito médico e saúde.",
  },
  {
    icon: "ri-eye-line",
    title: "Transparência Total",
    desc: "Comunicação clara e honesta sobre cada etapa do processo, sem surpresas ou termos difíceis.",
    highlight: "Você sabe exatamente o que está acontecendo.",
  },
  {
    icon: "ri-speed-line",
    title: "Agilidade e Eficiência",
    desc: "Processos ágeis e estratégicos para que seus direitos sejam garantidos no menor tempo possível.",
    highlight: "Celeridade sem abrir mão da qualidade.",
  },
  {
    icon: "ri-group-line",
    title: "Equipe Multidisciplinar",
    desc: "Equipe própria somada a uma rede de parceiros especializados para cobertura jurídica completa.",
    highlight: "A solução certa para cada caso.",
  },
];

const steps = [
  { num: "01", title: "Primeiro Contato", desc: "Entre em contato pelo WhatsApp ou formulário para um atendimento inicial." },
  { num: "02", title: "Análise do Caso", desc: "Avaliamos detalhadamente sua situação e identificamos as melhores estratégias." },
  { num: "03", title: "Estratégia Jurídica", desc: "Desenvolvemos o plano de ação mais eficiente e objetivo para o seu caso." },
  { num: "04", title: "Acompanhamento Contínuo", desc: "Monitoramos cada etapa do processo com total transparência e comunicação." },
];

function DiferencialCard({ item }: { item: typeof diferenciais[0] }) {
  return (
    <div
      className="group rounded-lg p-6 border border-white/10 hover:border-[#C8A63A]/40 transition-all duration-400 hover:-translate-y-1 flex flex-col"
      style={{ background: "rgba(255,255,255,0.04)", minHeight: "220px" }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-300 group-hover:scale-110"
          style={{ background: "linear-gradient(135deg, #C8A63A25, #E6D18A15)" }}
        >
          <i className={`${item.icon} text-xl`} style={{ color: "#C8A63A" }} />
        </div>
        <h4 className="font-bold text-white text-sm leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>
          {item.title}
        </h4>
      </div>
      <p className="text-white/60 text-xs leading-relaxed mb-4 flex-1" style={{ fontFamily: "'Inter', sans-serif" }}>
        {item.desc}
      </p>
      <div
        className="text-xs font-medium italic"
        style={{ color: "#E6D18A", fontFamily: "'Cormorant Garamond', serif", fontSize: "13px" }}
      >
        &ldquo;{item.highlight}&rdquo;
      </div>
    </div>
  );
}

export default function Diferenciais() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".diff-item").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0) translateX(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const cardWidth = el.scrollWidth / diferenciais.length;
      const idx = Math.round(el.scrollLeft / cardWidth);
      setActiveSlide(idx);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (idx: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / diferenciais.length;
    el.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
    setActiveSlide(idx);
  };

  return (
    <>
      {/* Diferenciais Section */}
      <section id="diferenciais" ref={sectionRef} className="py-24 bg-[#0A2472] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ background: "radial-gradient(circle, #4EA3D8, transparent)" }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5" style={{ background: "radial-gradient(circle, #C8A63A, transparent)" }} />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Header */}
          <div
            className="text-center mb-16 diff-item"
            style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-px bg-[#C8A63A]" />
              <span
                className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C8A63A]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Por Que Nos Escolher
              </span>
              <div className="w-10 h-px bg-[#C8A63A]" />
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Nossos Diferenciais
            </h2>
            <p className="text-white/60 max-w-xl mx-auto text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
              O que nos torna a escolha certa para proteger seus direitos.
            </p>
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" } as React.CSSProperties}
            >
              {diferenciais.map((item) => (
                <div
                  key={item.title}
                  className="snap-start flex-shrink-0"
                  style={{ width: "82vw", maxWidth: "320px" }}
                >
                  <DiferencialCard item={item} />
                </div>
              ))}
            </div>
            {/* Dots */}
            <div className="flex items-center justify-center gap-2 mt-5">
              {diferenciais.map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollTo(i)}
                  className="rounded-full transition-all duration-300 cursor-pointer"
                  style={{
                    width: activeSlide === i ? "20px" : "8px",
                    height: "8px",
                    background: activeSlide === i ? "#C8A63A" : "rgba(255,255,255,0.4)",
                    opacity: activeSlide === i ? 1 : 0.5,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Desktop Row */}
          <div className="hidden md:flex flex-row gap-4">
            {diferenciais.map((item, i) => (
              <div
                key={item.title}
                className="diff-item flex-1"
                style={{
                  opacity: 0,
                  transform: "translateY(20px)",
                  transition: `opacity 0.7s ease ${i * 0.1}s, transform 0.7s ease ${i * 0.1}s`,
                }}
              >
                <DiferencialCard item={item} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section className="py-24 bg-[#F2F2F2]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-px bg-[#C8A63A]" />
              <span
                className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C8A63A]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Processo
              </span>
              <div className="w-10 h-px bg-[#C8A63A]" />
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0A2472] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Como Funciona
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
              Do primeiro contato à resolução, acompanhamos você em cada passo.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            <div
              className="absolute top-10 left-1/4 right-1/4 h-px hidden lg:block"
              style={{ background: "linear-gradient(90deg, #C8A63A, #E6D18A, #C8A63A)" }}
            />
            {steps.map((step) => (
              <div key={step.num} className="relative text-center group">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 font-bold text-lg transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: "linear-gradient(135deg, #0A2472, #1F4ED8)",
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#E6D18A",
                    fontSize: "22px",
                  }}
                >
                  {step.num}
                </div>
                <h4
                  className="font-bold text-[#0A2472] mb-2 text-sm"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.title}
                </h4>
                <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
